import React, { useEffect, useId, useRef } from "react";
import { tabbableIn } from "../../utils/focusable";
import "./Modal.css";

export default function Modal({ open, onClose, title, children, closeDisabled = false }) {
  const dialogRef = useRef(null);
  // Where the last press on the overlay began. A drag that starts in a field and
  // ends outside the card still fires a click on the overlay, and that is not
  // someone asking to close.
  const pressStartedOnOverlayRef = useRef(false);
  const titleId = useId();

  // Move focus into the dialog on open and hand it back to whatever opened it
  // on close, so keyboard and screen reader users keep their place.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => {
      // The opener is gone when closing navigated away or redrew the page.
      if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  // Esc closes and Tab stays inside the dialog
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // A control inside the dialog already used this key, as the date picker
      // does when Esc closes its calendar.
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        if (!closeDisabled) onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const tabbable = tabbableIn(dialog);
      const active = document.activeElement;
      if (!tabbable.length) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      if (!dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, closeDisabled]);

  // Prevent background scroll when open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = original);
  }, [open]);

  if (!open) return null;

  const handleOverlayClose = () => {
    if (closeDisabled) return;
    onClose?.();
  };

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        pressStartedOnOverlayRef.current = e.target === e.currentTarget;
      }}
      onClick={() => {
        const pressStartedOnOverlay = pressStartedOnOverlayRef.current;
        pressStartedOnOverlayRef.current = false;
        if (pressStartedOnOverlay) handleOverlayClose();
      }}
    >
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : "Dialog"}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        ref={dialogRef}
      >
        <div className="modal-head">
          <h3 className="modal-title" id={titleId}>
            {title}
          </h3>
          <button
            type="button"
            className="modal-close"
            onClick={handleOverlayClose}
            aria-label="Close"
            disabled={closeDisabled}
          >
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
